import { ActivatedRouteSnapshot, Params, RouterStateSnapshot } from "@angular/router";
import { Injectable } from "@angular/core";
import { EMPTY, Observable, of } from "rxjs";
import { FiatDepositFacade } from "@modules/+new/payments/features/fiat-deposit/domain/+state/fiat-deposit.facade";
import { InfoService } from "@core-services/info.service";
import { catchError, filter, map, switchMap, take } from "rxjs/operators";
import { FiatDepositStepsData } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-steps-data.model";
import { FiatDepositParamNames } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-param-names.enum";
import { FiatDepositStepNames } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-step-names.enum";
import { FiatDepositStepValidationStatus } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-step-validation-status.model";
import { stringToBoolean } from "@helpers/functions";
import { TranslationService } from "@core-services/translation.service";
import { CryptocoinsState } from "@modules/+new/payments/shared-payments/services/cryptocoins-state.service";

@Injectable()
export class FiatDepositGuard {
	public constructor(
		private readonly fiatDepositFacade: FiatDepositFacade,
		private readonly infoService: InfoService,
		private readonly translationService: TranslationService,
		private readonly cryptocoinsState: CryptocoinsState
	) {}

	public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
		const stepName: FiatDepositStepNames = <FiatDepositStepNames>route.routeConfig.path;
		const fromSummary = stringToBoolean(route.queryParams?.[FiatDepositParamNames.FromSummary]);

		if (fromSummary) {
			return this.fiatDepositFacade
				.isStateValid$()
				.pipe(map((status: FiatDepositStepValidationStatus) => status.isStateValid));
		}

		return this.checkStepValidity(stepName, route.queryParams);
	}

	private checkStepValidity(stepName: FiatDepositStepNames, params: Params): Observable<boolean> {
		const allParamsStepsData: FiatDepositStepsData = this.prepareAllParamsStepsData(params);
		const fromBack = stringToBoolean(params?.[FiatDepositParamNames.FromBack]);
		const fromCheckout = stringToBoolean(params?.[FiatDepositParamNames.FromCheckout]);

		return this.resolveCryptoNetwork$(stepName, allParamsStepsData).pipe(
			switchMap(({ stepsData, redirectToSelectNetwork }) => {
				if (redirectToSelectNetwork) {
					this.fiatDepositFacade.navigateToStep(FiatDepositStepNames.SelectNetwork, {
						assetSymbol: stepsData.assetSymbol
					});
					return of(false);
				}

				return this.fiatDepositFacade.isStepStateValid$(stepName, stepsData, fromBack, fromCheckout);
			}),
			catchError(() => {
				this.inCaseErrorWhileProcessingFlow();
				return EMPTY;
			}),
			switchMap((status: FiatDepositStepValidationStatus) => {
				if (!status.isStateValid) {
					this.inCaseErrorWhileProcessingFlow();
					return of(false);
				}

				this.fiatDepositFacade.setStepsData(status.stepData);
				return of(true);
			})
		);
	}

	private resolveCryptoNetwork$(
		stepName: FiatDepositStepNames,
		stepsData: FiatDepositStepsData
	): Observable<{ stepsData: FiatDepositStepsData; redirectToSelectNetwork: boolean }> {
		if (!this.shouldResolveCryptoNetwork(stepName, stepsData)) {
			return of({ stepsData, redirectToSelectNetwork: false });
		}

		return this.cryptocoinsState.availableCryptoDepositList$.pipe(
			filter((cryptoList) => cryptoList?.length > 0),
			take(1),
			map((cryptoList) => {
				const selectedCrypto = cryptoList.find(
					(crypto) => crypto.symbol?.toLowerCase() === stepsData.assetSymbol.toLowerCase()
				);

				const networks = selectedCrypto?.networks;
				if (!networks?.length) {
					return { stepsData, redirectToSelectNetwork: false };
				}

				return networks.length === 1
					? { stepsData: { ...stepsData, networkId: networks[0].id }, redirectToSelectNetwork: false }
					: { stepsData, redirectToSelectNetwork: true };
			})
		);
	}

	private shouldResolveCryptoNetwork(stepName: FiatDepositStepNames, stepsData: FiatDepositStepsData): boolean {
		return !!(
			stepsData?.assetSymbol &&
			!stepsData?.networkId &&
			[
				FiatDepositStepNames.CryptoDepositSummary,
				FiatDepositStepNames.CryptoDepositWarning,
				FiatDepositStepNames.DepositCryptoWallet,
				FiatDepositStepNames.DepositNonDefaultAddress
			].includes(stepName)
		);
	}

	private prepareAllParamsStepsData(queryParams: Params): FiatDepositStepsData {
		return {
			fiatPid: queryParams?.[FiatDepositParamNames.FiatPid],
			walletGuid: queryParams?.[FiatDepositParamNames.Wallet],
			paymentOptionId: Number(queryParams?.[FiatDepositParamNames.PaymentOption]),
			depositAmount: Number(queryParams?.[FiatDepositParamNames.DepositAmount]),
			includingFees: stringToBoolean(queryParams?.[FiatDepositParamNames.IncludingFees]),
			bankProviderName: queryParams?.[FiatDepositParamNames.BankProvider],
			parentBankId: queryParams?.[FiatDepositParamNames.ParentBank],
			bankId: queryParams?.[FiatDepositParamNames.Bank],
			transactionId: queryParams?.[FiatDepositParamNames.Transaction],
			paymentUrl: queryParams?.[FiatDepositParamNames.PaymentUrl],
			assetSymbol: queryParams?.[FiatDepositParamNames.AssetSymbol],
			networkId: queryParams?.[FiatDepositParamNames.NetworkId]
		};
	}

	private inCaseErrorWhileProcessingFlow(): void {
		this.infoService.setWarning(this.translationService.translate("error_message_while_refreshing_deposit_flow"));
		this.fiatDepositFacade.navigateToStep(FiatDepositStepNames.SelectDepositType);
	}
}
